import React, { useRef, useEffect } from "react";
import { gsap } from "gsap"; // GSAP import करें
import { ScrollTrigger } from "gsap/ScrollTrigger"; // ScrollTrigger import करें

// GSAP plugins रजिस्टर करें (एक बार)
gsap.registerPlugin(ScrollTrigger);

const GallerySection = ({ galleryImages, currentSlide, setCurrentSlide }) => {
  // 1. सेक्शन कंटेनर और शीर्षक के लिए useRef बनाएं
  const sectionRef = useRef(null);
  const titleRef = useRef(null);

  // Navigation functions
  const nextGallerySlide = () => {
    setCurrentSlide((prev) => (prev + 1) % galleryImages?.length);
  };

  const prevGallerySlide = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + galleryImages?.length) % galleryImages?.length
    );
  };

  // useEffect Hook for GSAP Animations
  useEffect(() => {
    // सुनिश्चित करें कि ref मौजूद है
    if (!sectionRef.current) return;

    // 2. GSAP context बनाएं
    const context = gsap.context(() => {
      // --- 1. Gallery Title Animation (Text Reveal) ---
      // शीर्षक को नीचे से ऊपर की ओर फेड-इन करने के लिए (आपके दिए गए स्टाइल के समान)
      if (titleRef.current) {
        gsap.fromTo(
          titleRef.current,
          {
            y: 50,
            opacity: 0,
            clipPath: "inset(100% 0 0% 0)", // नीचे से क्लिप
          },
          {
            y: 0,
            opacity: 1,
            clipPath: "inset(0% 0 0% 0)", // पूरी तरह से अनक्लिप
            duration: 1.2,
            ease: "power2.out",
            scrollTrigger: {
              trigger: titleRef.current,
              start: "top 85%",
              end: "bottom 80%", // जब शीर्षक 80% पर पहुंचे तो एनीमेशन पूरा हो जाए
              toggleActions: "play none none none", // एक बार स्क्रॉल डाउन करने पर चल जाए
              // markers: true, // Debugging के लिए
            },
          }
        );
      }

      // --- 2. Gallery Layout Container Animation (Slide-in) ---
      // गैलरी लेआउट कंटेनर को नीचे से ऊपर की ओर स्लाइड-इन करें
      const layoutContainer = sectionRef.current.querySelector(
        ".gallery-layout-container"
      );

      if (layoutContainer) {
        gsap.fromTo(
          layoutContainer,
          {
            y: 100, // नीचे से शुरू
            opacity: 0,
          },
          {
            y: 0, // ऊपर की ओर स्लाइड
            opacity: 1,
            duration: 1.5,
            ease: "power3.out",
            scrollTrigger: {
              trigger: layoutContainer,
              start: "top 90%", // जब कंटेनर 90% पर पहुंचे
              toggleActions: "play none none none",
              // markers: true, // Debugging के लिए
            },
          }
        );
      }
    }, sectionRef); // Pass the ref to the context

    // Cleanup function
    return () => context.revert();
  }, []); // Empty dependency array means this runs once after initial render

  // सुनिश्चित करें कि गैलरी इमेजेज मौजूद हैं और कम से कम 2 इमेजेज हैं
  if (!galleryImages || galleryImages.length < 2) {
    return null;
  }

  // LOGIC CHANGE: Hardcoded images को currentSlide के आधार पर कैलकुलेट करें
  const leftImage = galleryImages[currentSlide];
  const rightImageIndex = (currentSlide + 1) % galleryImages.length;
  const rightImage = galleryImages[rightImageIndex];

  return (
    // sectionRef को सबसे बाहरी तत्व पर लगाएं
    <section
      ref={sectionRef}
      className="w-full bg-background-main py-[40px] sm:py-[60px] lg:py-[86px]"
    >
      <div className="w-full max-w-[1620px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-[30px] sm:gap-[40px] lg:gap-[50px] justify-start items-center w-full">
          {/* Gallery Title - titleRef को यहां लगाएं */}
          <h2
            ref={titleRef}
            className="text-[40px] sm:text-[60px] md:text-[80px] lg:text-hero font-[Parkinsans] font-bold leading-[56px] sm:leading-[84px] md:leading-[112px] lg:leading-hero text-center text-[#343163]"
          >
            Gallery
          </h2>

          {/* Gallery Slider/Layout - एनीमेशन के लिए क्लास जोड़ें */}
          <div className="relative w-full gallery-layout-container">
            <div className="flex flex-col lg:flex-row justify-start items-end gap-4 lg:gap-0 w-full">
              {/* Left Gallery Item (Bigger Image) */}
              <div className="flex flex-col gap-[15px] sm:gap-[18px] lg:gap-[20px] justify-start items-center w-full lg:w-[48%]">
                <img
                  src={leftImage?.src}
                  alt={leftImage?.title}
                  className="w-full h-[300px] sm:h-[450px] lg:h-[582px] object-cover rounded-[20px] md:rounded-2xl"
                />
                {/* Text and Divider */}
                <div className="flex flex-col gap-[8px] sm:gap-[10px] lg:gap-[12px] justify-start items-center w-full border-t-[1px] sm:border-t-[2px] border-solid border-accent-text pt-[10px] sm:pt-[12px] lg:pt-[14px]">
                  {/* GSAP में हॉरिजॉन्टल डिवाइडर को ग्रो कराने के लिए: */}
                  {/* यह एनीमेशन आपके द्वारा दिए गए कोड में था, लेकिन मैंने इसे simplicity के लिए यहाँ छोड़ दिया है, */}
                  {/* अगर आप इसे जोड़ना चाहते हैं, तो आपको इसे एक अलग useRef/query के साथ target करना होगा। */}
                  {/* फ़िलहाल मैं केवल मुख्य ब्लॉक एनिमेशन पर ध्यान केंद्रित कर रहा हूँ। */}
                  <div className="w-full h-[1px] sm:h-[2px] bg-accent-text rounded-xs horizontal-divider"></div>
                  <div className="flex flex-row justify-between items-center w-full">
                    <span className="text-[16px] lg:text-[20px] font-[Parkinsans] font-normal leading-md text-left text-accent-muted">
                      {leftImage?.subtitle}
                    </span>
                    <h3 className="text-[18px] lg:text-[24px] font-[Parkinsans] font-normal leading-xl text-left text-accent-text">
                      {leftImage?.title}
                    </h3>
                  </div>
                </div>
              </div>

              {/* Right Gallery Item (Smaller Image + Navigation) */}
              <div className="flex flex-col-reverse md:flex-col justify-center items-end w-full lg:w-[52%] lg:mt-[100px] gap-4">
                {/* Navigation Buttons */}
                <div className="flex flex-row justify-between md:justify-end items-center gap-[12px] w-full mt-[20px]">
                  <button
                    onClick={prevGallerySlide}
                    className="flex items-center justify-center w-[50px] h-[50px] sm:w-[65px] sm:h-[65px] lg:w-[80px] lg:h-[80px] border-[1px] border-solid border-[#343163] rounded-full bg-transparent hover:bg-accent-text hover:invert transition-all duration-200"
                    aria-label="Previous gallery image"
                  >
                    <img
                      src="/assets/icon/leftArrow.png"
                      alt="Previous"
                      className="w-[10px] md:w-auto"
                    />
                  </button>
                  <button
                    onClick={nextGallerySlide}
                    className="flex items-center justify-center w-[50px] h-[50px] sm:w-[65px] sm:h-[65px] lg:w-[80px] lg:h-[80px] border-[1px] border-solid border-[#343163] rounded-full bg-transparent hover:bg-accent-text hover:invert transition-all duration-200"
                    aria-label="Next gallery image"
                  >
                    <img
                      src="/assets/icon/rightArrow.png"
                      alt="Next"
                      className="w-[10px] md:w-auto"
                    />
                  </button>
                </div>
                {/* Right Image Display */}
                <div className="hidden md:flex flex-col gap-[15px] sm:gap-[18px] lg:gap-[20px] justify-start items-center w-full lg:w-[96%]">
                  <img
                    src={rightImage?.src}
                    alt={rightImage?.title}
                    className="w-full h-[200px] sm:h-[240px] lg:h-[274px] object-cover rounded-2xl"
                  />
                  {/* Text and Divider */}
                  <div className="flex flex-col gap-[8px] sm:gap-[10px] lg:gap-[12px] justify-start items-center w-full border-t-[1px] sm:border-t-[2px] border-solid border-accent-text pt-[10px] sm:pt-[12px] lg:pt-[14px]">
                    <div className="w-full h-[1px] sm:h-[2px] bg-accent-text rounded-xs"></div>
                    <div className="flex flex-row justify-between items-center w-full">
                      <span className="text-[16px] lg:text-[20px] font-[Parkinsans] font-normal leading-md text-left text-accent-muted">
                        {rightImage?.subtitle}
                      </span>
                      <h3 className="text-[18px] lg:text-[24px] font-[Parkinsans] font-normal leading-xl text-left text-accent-text">
                        {rightImage?.title}
                      </h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GallerySection;
